// pages/TravelExpense/TravelTripReportPanel.js
//
// Go-live parity (2026-09-24): audit 09-travel #3 — legacy "Trip Report" tab
// (frmTravelExpense.vb / frmTripReport): one report per destination with
// Overview, Contacts, Notes and "Summary of Important Takeaways", saved to
// ExpenseReport. Legacy reports are rich text (RTF); the API returns them as
// plain text and saves the entered text as simple RTF the desktop can open.
// The trip's travelers write; everyone who can view the trip reads.
//
// Backend: GET TravelExpense/{id}/reports, PUT TravelExpense/{id}/reports/{destinationId}

import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Input, Label, Button, Spinner, Alert } from "reactstrap";
import { ResponsiveTabs } from "../../Components/Common/DashboardKit";
import api from "../../api";
import { toCamel } from "./travelCase";

const SECTIONS = [
    ["overview", "Overview"],
    ["contacts", "Contacts"],
    ["notes", "Notes"],
    ["summary", "Summary of Important Takeaways"],
];

const fmtDate = (d) => { if (!d) return ""; try { return new Date(d).toLocaleDateString(); } catch { return ""; } };

const TravelTripReportPanel = ({ tripId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [active, setActive] = useState(null);
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true); setError(null);
        api.get(`TravelExpense/${tripId}/reports`)
            .then(({ data: raw }) => {
                const d = toCamel(raw);
                setData(d);
                const items = Array.isArray(d?.items) ? d.items : [];
                const next = {};
                items.forEach((it) => {
                    next[it.destinationId] = {
                        overview: it.overview || "", contacts: it.contacts || "",
                        notes: it.notes || "", summary: it.summary || "",
                    };
                });
                setDrafts(next);
                setActive((a) => (a != null && items.some((it) => it.destinationId === a) ? a : items[0]?.destinationId ?? null));
            })
            .catch((e) => setError(e?.response?.data?.error || e?.message || "Failed to load the trip report"))
            .finally(() => setLoading(false));
    }, [tripId]);
    useEffect(load, [load]);

    const items = Array.isArray(data?.items) ? data.items : [];
    const canWrite = !!data?.canWrite;
    const current = items.find((it) => it.destinationId === active);
    const draft = drafts[active] || {};

    const save = async () => {
        setBusy(true); setError(null); setNotice(null);
        try {
            await api.put(`TravelExpense/${tripId}/reports/${active}`, draft);
            setNotice("Changes saved successfully.");
            load();
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Save failed");
        } finally { setBusy(false); }
    };

    if (loading && !data) return <div className="text-center py-4"><Spinner color="primary" /></div>;
    if (items.length === 0) return (
        <>
            {error && <Alert color="danger">{error}</Alert>}
            <div className="text-muted">This trip has no destinations, so there is no trip report.</div>
        </>
    );

    return (
        <div>
            {error && <Alert color="danger">{error}</Alert>}
            {notice && <Alert color="success">{notice}</Alert>}
            <ResponsiveTabs size="sm" activeKey={active} onChange={(k) => { setActive(k); setNotice(null); }}
                tabs={items.map((it) => ({ key: it.destinationId, label: `Visit: ${it.company || it.cityStateCountry || it.destinationId}` }))} />
            {current && (
                <div className="text-muted small mb-2">
                    {current.cityStateCountry} {current.startDate ? `· ${fmtDate(current.startDate)} – ${fmtDate(current.endDate)}` : ""}
                    {current.lastSavedByName ? ` · last saved by ${current.lastSavedByName}` : " · no report written yet"}
                </div>
            )}
            <Row className="g-3">
                {SECTIONS.map(([key, label]) => (
                    <Col md={6} key={key}>
                        <Label className="mb-1 fw-semibold">{label}</Label>
                        {canWrite ? (
                            <Input type="textarea" rows={6} value={draft[key] ?? ""} disabled={busy}
                                onChange={(e) => setDrafts((d) => ({ ...d, [active]: { ...(d[active] || {}), [key]: e.target.value } }))} />
                        ) : (
                            <div className="border rounded p-2 bg-light" style={{ whiteSpace: "pre-wrap", minHeight: 80 }}>
                                {draft[key] || <span className="text-muted">—</span>}
                            </div>
                        )}
                    </Col>
                ))}
            </Row>
            {canWrite && (
                <div className="mt-3 d-flex align-items-center gap-2">
                    <Button color="primary" disabled={busy || active == null} onClick={save}>
                        {busy ? <Spinner size="sm" /> : "Save changes"}
                    </Button>
                    <span className="text-muted small">Saves the report for this destination. Formatting of a report written in the desktop app is replaced by plain text when you save it here.</span>
                </div>
            )}
        </div>
    );
};

export default TravelTripReportPanel;
